import { Delete } from '@mui/icons-material'
import { Box, Chip, Typography, useTheme } from '@mui/material'
import { styled } from '@mui/material/styles'
import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { FacilityTypes } from 'src/enums'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { getThemeColor } from 'src/utils'

interface IUserScopesColumnProps {
  scopes: any[]
  onRowClick?: (scopeId: number, scopeType: number) => void
  isLastRow?: boolean
}

function scopeDisplaySortLabel(name: string | undefined, translate: (key: string, options?: { ns: string }) => string) {
  if (!name) return ''
  return `${name}`.substring(0, 3) == 'all' ? translate(name, { ns: 'common' }) : name
}

function compareScopesAlphabetically(
  a: { name?: string },
  b: { name?: string },
  translate: (key: string, options?: { ns: string }) => string,
) {
  return scopeDisplaySortLabel(a.name, translate).localeCompare(scopeDisplaySortLabel(b.name, translate), undefined, {
    sensitivity: 'base',
  })
}

const UserScopesColumn: React.FC<IUserScopesColumnProps> = ({ scopes, onRowClick, isLastRow }) => {
  const theme = useTheme()
  const { t: translate } = useTranslation('common')

  const scopeElements = useMemo(() => {
    const localityScopes: any[] = []
    const serviceStationScopes: any[] = []
    const storageScopes: any[] = []
    const projectScopes: any[] = []

    scopes.forEach(scope => {
      if (scope.type === FacilityTypes.locality || scope.type === FacilityTypes.localityGroup) {
        localityScopes.push(scope)
      } else if (scope.type === FacilityTypes.serviceStation || scope.type === FacilityTypes.serviceStationGroup) {
        serviceStationScopes.push(scope)
      } else if (scope.type === FacilityTypes.storage) {
        storageScopes.push(scope)
      } else if (scope.type === FacilityTypes.ProjectPlanning) {
        projectScopes.push(scope)
      }
    })
    const sortGroup = (list: any[]) => list.sort((a, b) => compareScopesAlphabetically(a, b, translate))
    sortGroup(localityScopes)
    sortGroup(serviceStationScopes)
    sortGroup(storageScopes)
    sortGroup(projectScopes)
    return { localityScopes, serviceStationScopes, storageScopes, projectScopes }
  }, [scopes, translate])

  return (
    <Flex.Row flexWrap={'wrap'} gap={1}>
      {scopeElements.localityScopes.length > 0 && (
        <ScopeChip
          isLastRow={isLastRow}
          theme={theme}
          scopes={scopeElements.localityScopes}
          translate={translate}
          onRowClick={onRowClick}
        />
      )}
      {scopeElements.serviceStationScopes.length > 0 && (
        <ScopeChip
          scopes={scopeElements.serviceStationScopes}
          theme={theme}
          translate={translate}
          onRowClick={onRowClick}
          isLastRow={isLastRow}
        />
      )}
      {scopeElements.storageScopes.length > 0 && (
        <ScopeChip
          isLastRow={isLastRow}
          scopes={scopeElements.storageScopes}
          theme={theme}
          translate={translate}
          onRowClick={onRowClick}
        />
      )}
      {scopeElements.projectScopes.length > 0 && (
        <ScopeChip
          isLastRow={isLastRow}
          scopes={scopeElements.projectScopes}
          theme={theme}
          translate={translate}
          onRowClick={onRowClick}
        />
      )}
    </Flex.Row>
  )
}

const ScopeChip = ({ scopes, theme, translate, onRowClick, isLastRow }) => {
  const scope = scopes?.[0] ?? {}

  return (
    <Box
      id={`${scope.id}-${scope.type}`}
      sx={{
        position: 'relative',
        '&:hover .localities-list': {
          display: scopes.length > 1 ? 'block' : 'none',
        },
      }}
    >
      <StyledScopeChip
        label={
          `${scope.name}`.substring(0, 3) == 'all'
            ? translate(scope.name, { ns: 'common' })
            : scope.name + (scopes.length > 1 ? ', ...' : '')
        }
        scopeType={scope.type}
        onDelete={onRowClick && scopes.length == 1 ? () => onRowClick(scope.id, scope.type) : undefined}
        deleteIcon={
          <StyledDeleteIcon
            style={{ color: getThemeColor(theme, MUIThemeColors.primaryMain) }} // need to override color style
          />
        }
      />
      {scopes.length > 0 && (
        <StyledLocalitiesList className="localities-list" sx={isLastRow ? { bottom: '40px' } : {}}>
          {scopes.map(scope => (
            <Box
              key={scope.id}
              sx={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', padding: '2px' }}
            >
              <Typography>
                {`${scope.name}`.substring(0, 3) == 'all' ? translate(scope.name, { ns: 'common' }) : scope.name}
              </Typography>
              <StyledDeleteIcon
                sx={{ paddingLeft: '5px' }}
                onClick={evt => {
                  evt.preventDefault()
                  evt.stopPropagation()
                  onRowClick && onRowClick(scope.id, scope.type)
                }}
              />
            </Box>
          ))}
        </StyledLocalitiesList>
      )}
    </Box>
  )
}

export default UserScopesColumn

//styled components
const StyledDeleteIcon = styled(Delete)(({ theme }) => ({
  color: getThemeColor(theme, MUIThemeColors.successDark),
  height: 19,
  width: 25,
  cursor: 'pointer',
}))

// Styled component for the localities list popover
const StyledLocalitiesList = styled(Box)(({ theme }) => ({
  display: 'none',
  position: 'fixed',
  backgroundColor: theme.palette.background.paper,
  boxShadow: theme.shadows[3],
  padding: '8px',
  borderRadius: '4px',
  zIndex: 1000,
  color: theme.palette.primaryMain[theme.palette.mode],
  '&:hover': {
    display: 'block',
  },
}))

// Styled component for the Chip with dynamic background and color
const StyledScopeChip = styled(Chip)<{ scopeType: number }>(({ theme, scopeType }) => ({
  background:
    scopeType === FacilityTypes.locality
      ? `${getThemeColor(theme, MUIThemeColors.primaryLight)}44`
      : scopeType === FacilityTypes.serviceStation
      ? `${getThemeColor(theme, MUIThemeColors.successLight)}44`
      : `${getThemeColor(theme, MUIThemeColors.warningLight)}44`,
  color: getThemeColor(theme, MUIThemeColors.primaryDark),
  marginBottom: 4,
  marginTop: 4,
}))
